// Copyright 2022 - 2023 Fewcha. All rights reserved.

import { useAppSelector } from "app/hooks";
import { selectConfig } from "features/config/configReducer";
import { selectTokens } from "features/tokens/tokensReducer";
import { BlockchainToken } from "features/tokens/types";
import { selectWallet } from "features/wallet/walletReducer";
import lodash from "lodash";
import React, { useState } from "react";
import cn from "services/cn";
import styled from "styled-components";

const Card = styled.div`
  transition: 0.3s;
  position: relative;
  &::before {
    content: "";
    background: linear-gradient(90deg, rgba(255, 255, 255, 0) 0%, #ffffff 50.52%, rgba(255, 255, 255, 0) 100%);
    display: inline-block;
    height: 2px;
    position: absolute;
    top: -1px;
    left: 50%;
    width: 100%;
    transform: translate(-50%, 0);
  }
  &::after {
    content: "";
    background: linear-gradient(90deg, rgba(255, 255, 255, 0) 0%, #ffffff 50.52%, rgba(255, 255, 255, 0) 100%);
    width: 100%;
    display: inline-block;
    height: 2px;
    position: absolute;
    bottom: -1px;
    left: 50%;
    transform: translate(-50%, 0);
  }
`;

const CardImage = styled.div`
  position: relative;
  &.has-after {
    &::after {
      content: "";
      background: linear-gradient(90deg, rgba(255, 255, 255, 0) 0%, #ffffff 50.52%, rgba(255, 255, 255, 0) 100%);
      display: inline-block;
      position: absolute;
      height: 2px;
      width: 100%;
      bottom: -10px;
      left: 50%;
      transform: translate(-50%, 0);
      @media (min-width: 1024px) {
        width: 2px;
        height: 100%;
        background: linear-gradient(rgba(255, 255, 255, 0) 0%, #ffffff 50.52%, rgba(255, 255, 255, 0) 100%);
        right: -10px;
        top: 50%;
        left: auto;
        transform: translate(0, -50%);
      }
    }
  }
`;

const List: React.FC = () => {
  const tokens = useAppSelector(selectTokens);
  const wallet = useAppSelector(selectWallet);
  const config = useAppSelector(selectConfig);

  if (!wallet.isConnected || tokens.loading) return null;

  const list = tokens.tokens.filter((token) => token.creator === config.base.COLLECTION_CREATOR);

  if (!list.length) return null;

  return (
    <section className="text-white max-w-[1024px] px-6 mx-auto mt-[116px]">
      <h3 className="text-[36px]">My Perks</h3>
      <div className="mt-9 relative max-w-[80%] lg:max-w-[1024px] mx-auto gap-11">
        {lodash.chunk(list, 2).map((row, index) => (
          <div key={index}>
            {row.map((token, y) => (
              <Token token={token} key={y} />
            ))}
            <div className="clear-both"></div>
          </div>
        ))}
      </div>
      <div className="clear-both"></div>
    </section>
  );
};

const Token: React.FC<{ token: BlockchainToken }> = ({ token }) => {
  const config = useAppSelector(selectConfig);

  const [show, setShow] = useState(false);

  const same = config.tokens.find((item) => token.name.includes(item.name));

  return (
    <Card
      id="my-perks"
      className="lg:w-[45%] lg:ml-[2.5%] lg:mr-[2.5%] mb-8 lg:float-left fewcha--token-card rounded-[24px] flex flex-col p-9 text-white gap-12"
    >
      <div className="flex gap-6 lg:items-center w-full lg:justify-between flex-col lg:flex-row">
        <CardImage
          className={cn("lg:w-[276px] flex gap-6", {
            "has-after": same,
          })}
        >
          <div className="w-[74px]">
            <img src={token.uri} alt={token.name} />
          </div>

          <div>
            <div className="text-[20px] flex-1">{token.name}</div>
            <div className="text-[15px] mt-2">Amount: {token.supply}</div>
          </div>
        </CardImage>
        {same && (
          <div
            className="flex items-center gap-1 cursor-pointer select-none"
            onClick={() => {
              setShow(!show);
            }}
          >
            <div>View</div>
            <div
              className={cn({
                "rotate-180": show,
              })}
            >
              <svg width="15" height="7" viewBox="0 0 15 7" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M2.2628 0.316669C1.8854 -0.0068114 1.31723 0.036894 0.993752 0.414287C0.670272 0.79168 0.713977 1.35985 1.09137 1.68333L2.2628 0.316669ZM13.9295 1.68333C14.3069 1.35985 14.3506 0.791681 14.0271 0.414288C13.7036 0.0368945 13.1354 -0.00681091 12.758 0.316669L13.9295 1.68333ZM8.05274 5.53515L7.46703 4.85182L8.05274 5.53515ZM1.09137 1.68333L6.38238 6.21848L7.5538 4.85182L2.2628 0.316669L1.09137 1.68333ZM8.63845 6.21848L13.9295 1.68333L12.758 0.316669L7.46703 4.85182L8.63845 6.21848ZM6.38238 6.21848C7.03149 6.77486 7.98934 6.77487 8.63845 6.21848L7.46703 4.85182C7.492 4.83042 7.52884 4.83042 7.5538 4.85182L6.38238 6.21848Z"
                  fill="white"
                />
              </svg>
            </div>
          </div>
        )}
      </div>
      {show && same && (
        <div className="lg:ml-[60px]">
          <ul className="list-disc list-inside leading-[19.5px]">
            {same.description.map((text, index) => {
              return <li key={index}>{text}</li>;
            })}
          </ul>
        </div>
      )}
    </Card>
  );
};

export default List;
